import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME } from "./attributes";

import {
  SharedCard,
  SharedBack,
  SharedDate,
  SharedRadio,
  SharedCountry,
  SharedValidate,
} from "@/components/shared";
import { UIInput, UIButton } from "@/components/ui";

import { VerificationKybComponent } from "@/views/verification/model";
import {
  KybBasicInfoFieldsFactory,
  KybBasicInfoRequestFactory,
  KybBasicInfoResponseFactory,
} from "@/shared/repository/modules/kyb/factory";

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    SharedCard,
    SharedBack,
    SharedDate,
    SharedRadio,
    SharedCountry,
    SharedValidate,
    UIInput,
    UIButton,
  },
  props: {
    component: {
      type: <PropType<VerificationKybComponent>>String,
      default: "",
    },
  },
  emits: {
    "update:component": null,
  },
  data() {
    return {
      isValidate: false,
      isLoading: false,
      userRepo: this.$projectServices.userRepo,
      kybRepository: this.$projectServices.kybRepository,
      kybBasicInfo: KybBasicInfoResponseFactory(),
      kybBasicInfoFields: KybBasicInfoFieldsFactory(),
      authorized_capital_amount: "",
      authorized_capital_currency: "",
    };
  },
  async created(): Promise<void> {
    try {
      this.isLoading = true;

      this.kybBasicInfo = await this.kybRepository.basicInfo(
        this.userRepo.userInfo.id
      );

      // Parse capital fields if they exist
      if (this.kybBasicInfo.capital !== null) {
        this.authorized_capital_amount = this.kybBasicInfo.capital.toString();
      }
      if (this.kybBasicInfo.capital_currency) {
        this.authorized_capital_currency = this.kybBasicInfo.capital_currency;
      }
      
      // Update validation state after parsing
      this.updateCapitalValidation();
    } catch (e) {
      console.log(e);
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    isReadonly(): boolean {
      return this.userRepo.isReadonlyKyb;
    },

    isDisabled(): boolean {
      return Object.values(this.kybBasicInfoFields).some((field) => !field);
    },

    isContinueDisabled(): boolean {
      return this.isLoading;
    },

    authorizedCapitalCombined(): string {
      if (this.authorized_capital_amount && this.authorized_capital_currency) {
        return `${this.authorized_capital_amount} ${this.authorized_capital_currency}`;
      }
      return "";
    },
  },
  watch: {
    authorized_capital_amount() {
      this.updateCapitalValidation();
    },
    authorized_capital_currency() {
      this.updateCapitalValidation();
    },
  },
  methods: {
    updateCapitalValidation() {
      this.kybBasicInfoFields.capital = !!(this.authorized_capital_amount && this.authorized_capital_amount !== "");
      this.kybBasicInfoFields.capital_currency = !!(this.authorized_capital_currency && this.authorized_capital_currency !== "");
    },
    handleBack(): void {
      this.$emit("update:component", VerificationKybComponent.kybCompany);
    },

    async handleContinue(): Promise<void> {
      try {
        this.isLoading = true;

        if (this.isDisabled) {
          this.isValidate = true;

          return;
        }

        if (!this.isReadonly) {
          const kybData = { ...this.kybBasicInfo };
          kybData.capital = this.authorized_capital_amount ? Number(this.authorized_capital_amount) : null;
          kybData.capital_currency = this.authorized_capital_currency || null;
          
          await this.kybRepository.basicInfoUpdate(
            KybBasicInfoRequestFactory(kybData)
          );
        }

        this.$emit(
          "update:component",
          VerificationKybComponent.kybRegistrationInformation
        );
      } catch (e) {
        console.log(e);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
